import { HugeiconsIcon } from '@hugeicons/react';
import {
  AiBrain01Icon,
  CheckmarkCircle02Icon,
  CircleIcon,
  DashboardSpeedIcon,
  FileValidationIcon,
  Link02Icon,
  SecurityCheckIcon,
  Target01Icon,
} from '@hugeicons/core-free-icons';
import { useEffect, useMemo, useState } from 'react';
import { api } from '@/lib/api';
import { DemoDataBadge } from '@/components/shared/DemoDataBadge';
import { MotionSection, StaggerContainer, StaggerItem } from '@/components/shared/MotionSection';
import { OperationalPageHeader, OperationalStatCard, operationalCardClass } from '@/components/shared/OperationalUI';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';

const STATUS_VARIANT = {
  Operable: 'default',
  Instrumentado: 'secondary',
  Preparado: 'outline',
};

export default function OperatingSystem() {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get('/operating-system').then(({ data: response }) => setData(response));
  }, []);

  const averages = useMemo(() => {
    if (!data) return { baseline: 0, readiness: 0 };
    const count = data.capabilities.length || 1;
    return {
      baseline: data.capabilities.reduce((sum, item) => sum + item.baseline, 0) / count,
      readiness: Math.round(data.capabilities.reduce((sum, item) => sum + item.readiness, 0) / count),
    };
  }, [data]);

  if (!data) {
    return (
      <div className="mx-auto flex max-w-7xl flex-col gap-6 px-6 py-10">
        <Skeleton className="h-32 rounded-3xl" />
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          {[...Array(8)].map((_, index) => (
            <Skeleton key={index} className="h-56 rounded-2xl" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto flex max-w-7xl flex-col gap-8 px-6 py-10">
      <OperationalPageHeader
        title="Sistema operativo de IA responsable"
        subtitle="Centro de madurez, evidencia y mejora continua. El nivel 5 se acredita con resultados sostenidos, no solo con funcionalidades disponibles."
        actions={<DemoDataBadge />}
      />

      <StaggerContainer className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4" stagger={0.04}>
        <StaggerItem>
          <OperationalStatCard
            icon={DashboardSpeedIcon}
            label="Línea base"
            value={averages.baseline.toFixed(1)}
            suffix="/5"
            subtitle="Evaluación integral inicial"
          />
        </StaggerItem>
        <StaggerItem>
          <OperationalStatCard
            icon={CheckmarkCircle02Icon}
            label="Preparación de plataforma"
            value={averages.readiness}
            suffix="%"
            subtitle="Capacidades disponibles en VIA"
            tone="blue"
          />
        </StaggerItem>
        <StaggerItem>
          <OperationalStatCard
            icon={Target01Icon}
            label="Meta institucional"
            value="5.0"
            suffix="/5"
            subtitle="Madurez basada en evidencia"
            tone="success"
          />
        </StaggerItem>
        <StaggerItem>
          <OperationalStatCard
            icon={Link02Icon}
            label="Integraciones externas"
            value={data.integrations.filter((item) => !item.last_sync).length}
            subtitle="Pendientes de conexión corporativa"
            tone="alert"
          />
        </StaggerItem>
      </StaggerContainer>

      <MotionSection delay={0.06}>
        <Alert className="rounded-2xl border-primary/15 bg-primary/[0.03]">
          <HugeiconsIcon icon={SecurityCheckIcon} className="size-4" aria-hidden="true" />
          <AlertTitle>Lectura responsable del indicador</AlertTitle>
          <AlertDescription>{data.data_notice}</AlertDescription>
        </Alert>
      </MotionSection>

      <Tabs defaultValue="capabilities" className="flex flex-col gap-6">
        <TabsList className="h-auto w-full justify-start overflow-x-auto rounded-xl bg-muted/50 p-1 sm:w-fit">
          <TabsTrigger className="shrink-0" value="capabilities">
            Capacidades
          </TabsTrigger>
          <TabsTrigger className="shrink-0" value="roadmap">
            Hoja de ruta
          </TabsTrigger>
          <TabsTrigger className="shrink-0" value="integrations">
            Integraciones
          </TabsTrigger>
          <TabsTrigger className="shrink-0" value="transparency">
            Transparencia
          </TabsTrigger>
        </TabsList>

        <TabsContent value="capabilities">
          <StaggerContainer className="grid gap-4 md:grid-cols-2" stagger={0.04}>
            {data.capabilities.map((capability) => (
              <StaggerItem key={capability.id}>
                <Card className={operationalCardClass}>
                  <CardHeader>
                    <div className="flex items-start justify-between gap-4">
                      <div>
                        <CardTitle>{capability.name}</CardTitle>
                        <CardDescription>{capability.owner}</CardDescription>
                      </div>
                      <Badge variant={STATUS_VARIANT[capability.status] || 'outline'}>{capability.status}</Badge>
                    </div>
                  </CardHeader>
                  <CardContent className="flex flex-col gap-5">
                    <div>
                      <div className="mb-2 flex items-center justify-between text-xs text-muted-foreground">
                        <span>Preparación de plataforma</span>
                        <span className="font-semibold text-foreground">{capability.readiness}%</span>
                      </div>
                      <Progress value={capability.readiness} />
                    </div>
                    <div className="flex flex-col gap-2">
                      {capability.evidence.map((item) => (
                        <div key={item} className="flex items-start gap-2 text-sm">
                          <HugeiconsIcon
                            icon={CheckmarkCircle02Icon}
                            className="mt-0.5 size-4 shrink-0 text-success"
                            aria-hidden="true"
                          />
                          <span>{item}</span>
                        </div>
                      ))}
                    </div>
                    <div className="rounded-xl border border-warning/20 bg-warning/5 p-3">
                      <p className="text-xs font-semibold text-warning">Puerta para acreditar nivel 5</p>
                      <p className="mt-1 text-xs leading-relaxed text-muted-foreground">{capability.next_gate}</p>
                    </div>
                  </CardContent>
                </Card>
              </StaggerItem>
            ))}
          </StaggerContainer>
        </TabsContent>

        <TabsContent value="roadmap">
          <div className="flex flex-col gap-4">
            {data.roadmap.map((phase, index) => (
              <Card key={phase.phase} className={operationalCardClass}>
                <CardHeader>
                  <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
                    <div className="flex items-start gap-4">
                      <div className="flex size-10 shrink-0 items-center justify-center rounded-xl bg-primary/10 font-display font-bold text-primary">
                        {index + 1}
                      </div>
                      <div>
                        <CardTitle>{phase.phase}</CardTitle>
                        <CardDescription>
                          {phase.horizon} · {phase.owner}
                        </CardDescription>
                      </div>
                    </div>
                    <Badge variant="outline">{phase.progress}% preparado</Badge>
                  </div>
                </CardHeader>
                <CardContent className="grid gap-5 lg:grid-cols-[1fr_1.4fr]">
                  <div>
                    <p className="text-sm leading-relaxed">{phase.outcome}</p>
                    <Progress value={phase.progress} className="mt-4" />
                  </div>
                  <div className="grid gap-2 sm:grid-cols-2">
                    {phase.deliverables.map((deliverable) => (
                      <div key={deliverable.name} className="rounded-xl border border-border p-3">
                        <p className="text-sm font-medium">{deliverable.name}</p>
                        <p className="mt-1 text-xs text-muted-foreground">{deliverable.status}</p>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="integrations">
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {data.integrations.map((integration) => (
              <Card key={integration.id} className={operationalCardClass}>
                <CardHeader>
                  <div className="flex items-center justify-between gap-3">
                    <div className="flex size-10 items-center justify-center rounded-xl bg-primary/10 text-primary">
                      <HugeiconsIcon icon={Link02Icon} className="size-5" aria-hidden="true" />
                    </div>
                    <Badge variant="outline">{integration.status}</Badge>
                  </div>
                  <CardTitle className="pt-3">{integration.name}</CardTitle>
                  <CardDescription>{integration.purpose}</CardDescription>
                </CardHeader>
                <CardContent className="flex flex-col gap-2 text-xs text-muted-foreground">
                  <p>Modo: {integration.mode}</p>
                  <p>Responsable: {integration.owner}</p>
                  <p>Última sincronización: {integration.last_sync || 'Nunca · no conectada'}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="transparency">
          <div className="grid gap-5 lg:grid-cols-2">
            {data.transparency_cards.map((card) => (
              <Card key={card.system} className={operationalCardClass}>
                <CardHeader>
                  <div className="flex items-center justify-between gap-4">
                    <div className="flex size-10 items-center justify-center rounded-xl bg-primary/10 text-primary">
                      <HugeiconsIcon icon={FileValidationIcon} className="size-5" aria-hidden="true" />
                    </div>
                    <Badge variant="outline">{card.status}</Badge>
                  </div>
                  <CardTitle className="pt-3">{card.system}</CardTitle>
                  <CardDescription>{card.purpose}</CardDescription>
                </CardHeader>
                <CardContent className="grid gap-3 text-sm">
                  <TransparencyRow label="Supervisión humana" value={card.human_oversight} />
                  <TransparencyRow label="Datos" value={card.data} />
                  <TransparencyRow label="Limitaciones" value={card.limitations} />
                </CardContent>
              </Card>
            ))}
            <Card className={`${operationalCardClass} border-dashed`}>
              <CardHeader>
                <div className="flex size-10 items-center justify-center rounded-xl bg-secondary/10 text-secondary">
                  <HugeiconsIcon icon={AiBrain01Icon} className="size-5" aria-hidden="true" />
                </div>
                <CardTitle className="pt-3">Informe anual de IA responsable</CardTitle>
                <CardDescription>
                  Plantilla lista para consolidar adopción, valor, incidentes, equidad, controles y aprendizaje.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <HugeiconsIcon icon={CircleIcon} className="size-4" aria-hidden="true" />
                  Requiere cerrar el primer ciclo anual de evidencia.
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}

function TransparencyRow({ label, value }) {
  return (
    <div className="rounded-xl bg-muted/50 p-3">
      <p className="text-xs font-semibold text-muted-foreground">{label}</p>
      <p className="mt-1 leading-relaxed">{value}</p>
    </div>
  );
}
